import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  CAlert,
  CBadge,
  CButton,
  CFormInput,
  CSpinner,
} from '@coreui/react';
import {
  Search,
  Pause,
  RefreshCw,
  Bot,
  MessageSquare,
  Activity,
  Eye,
  FileText,
  CheckSquare,
  Crown,
  CalendarDays,
  Folder,
  Brain,
  Users,
  Building2,
  Users2,
  Server,
  Radar,
  Factory,
  GitBranch,
  FlaskConical,
  MessageCircle,
  Command,
} from 'lucide-react';
import './AgentMissionControlBoard.scss';

const BOARD_W = 980;
const BOARD_H = 640;

const floorZones = [
  { id: 'north-left', x: 150, y: 90, w: 180, h: 120 },
  { id: 'north-center', x: 410, y: 78, w: 200, h: 130 },
  { id: 'mid-left', x: 115, y: 245, w: 230, h: 120 },
  { id: 'mid-center', x: 360, y: 250, w: 290, h: 120 },
  { id: 'south-center', x: 360, y: 400, w: 220, h: 120 },
  { id: 'south-right', x: 675, y: 390, w: 140, h: 120 },
];

const navItems = [
  [Bot, 'Tasks'],
  [Bot, 'Agents'],
  [FileText, 'Content'],
  [CheckSquare, 'Approvals'],
  [Crown, 'Council'],
  [CalendarDays, 'Calendar'],
  [Folder, 'Projects'],
  [Brain, 'Memory'],
  [FileText, 'Docs'],
  [Users, 'People'],
  [Building2, 'Office'],
  [Users2, 'Team'],
  [Server, 'System'],
  [Radar, 'Radar'],
  [Factory, 'Factory'],
  [GitBranch, 'Pipeline'],
  [FlaskConical, 'Lab'],
  [MessageCircle, 'Feedback'],
];

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const randomPoint = (zone) => ({
  x: zone.x + 20 + Math.random() * (zone.w - 40),
  y: zone.y + 16 + Math.random() * (zone.h - 32),
});

const nearestZone = (x, y) =>
  floorZones.reduce(
    (best, zone) => {
      const cx = zone.x + zone.w / 2;
      const cy = zone.y + zone.h / 2;
      const d = Math.hypot(cx - x, cy - y);
      return d < best.distance ? { zone, distance: d } : best;
    },
    { zone: floorZones[0], distance: Infinity },
  ).zone;

const normalizeStatus = (status) => String(status || 'inactive').toLowerCase();

const toOfficeStatus = (status) => {
  if (status === 'active') return 'online';
  if (status === 'busy') return 'busy';
  return 'offline';
};

const statusText = (status) => {
  if (status === 'busy') return 'Busy';
  if (status === 'online') return 'Online';
  if (status === 'offline') return 'Offline';
  return 'Idle';
};

const agentColor = (name) => {
  const palette = ['#8b5cf6', '#3b82f6', '#22c55e', '#eab308', '#06b6d4', '#f97316', '#ec4899'];
  let sum = 0;
  for (let i = 0; i < name.length; i += 1) sum += name.charCodeAt(i);
  return palette[sum % palette.length];
};

const AgentMissionControlBoard = ({
  agents,
  loading,
  error,
  activityItems,
  activityLoading,
  officeName,
  onCreateAgent,
  onOpenAgent,
  onToggleStatus,
  onDeleteAgent,
  onRefresh,
}) => {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [positions, setPositions] = useState({});
  const targetsRef = useRef({});

  // Hydrate incoming API records with positions + status labels needed by the scene.
  const hydratedAgents = useMemo(
    () =>
      agents.map((agent, i) => {
        const zone = floorZones[i % floorZones.length];
        const point = randomPoint(zone);
        return {
          ...agent,
          officeStatus: toOfficeStatus(normalizeStatus(agent.status)),
          x: typeof agent.x === 'number' ? agent.x : point.x,
          y: typeof agent.y === 'number' ? agent.y : point.y,
        };
      }),
    [agents],
  );

  useEffect(() => {
    if (!hydratedAgents.length) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => prev || hydratedAgents[0].id);
  }, [hydratedAgents]);

  useEffect(() => {
    const nextPositions = {};
    const nextTargets = {};
    hydratedAgents.forEach((agent, i) => {
      nextPositions[agent.id] = { x: agent.x, y: agent.y };
      nextTargets[agent.id] = randomPoint(floorZones[(i + 1) % floorZones.length]);
    });
    targetsRef.current = nextTargets;
    setPositions(nextPositions);
  }, [hydratedAgents]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setPositions((prev) => {
        const next = { ...prev };
        const updatedTargets = { ...targetsRef.current };

        hydratedAgents.forEach((agent) => {
          const current = prev[agent.id] || { x: agent.x, y: agent.y };
          const target = updatedTargets[agent.id] || current;
          const dx = target.x - current.x;
          const dy = target.y - current.y;
          const dist = Math.hypot(dx, dy);
          const speed = agent.officeStatus === 'busy' ? 1.15 : agent.officeStatus === 'offline' ? 0.18 : 0.7;

          if (dist < 6) {
            const zone =
              Math.random() > 0.7
                ? floorZones[Math.floor(Math.random() * floorZones.length)]
                : nearestZone(current.x, current.y);
            updatedTargets[agent.id] = randomPoint(zone);
          } else {
            next[agent.id] = {
              x: clamp(current.x + (dx / dist) * speed, 24, BOARD_W - 24),
              y: clamp(current.y + (dy / dist) * speed, 24, BOARD_H - 24),
            };
          }
        });

        targetsRef.current = updatedTargets;
        return next;
      });
    }, 55);

    return () => window.clearInterval(timer);
  }, [hydratedAgents]);

  const visibleAgents = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return hydratedAgents;
    return hydratedAgents.filter((agent) => {
      const haystack = [agent.name, agent.personality, agent.status, agent.role].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [hydratedAgents, query]);

  const activeAgent = hydratedAgents.find((agent) => agent.id === selectedId) || hydratedAgents[0] || null;

  // Human-readable relative timestamps for the activity feed.
  const relativeTime = (isoValue) => {
    if (!isoValue) return 'just now';
    const ms = Date.now() - new Date(isoValue).getTime();
    if (!Number.isFinite(ms) || ms < 0) return 'just now';
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="agent-mission-control">
      <div className="agent-mission-control__shell">
        <aside className="agent-mission-control__nav">
          <div className="agent-mission-control__brand">
            <Command size={16} />
            <span>{officeName}</span>
          </div>
          <div className="agent-mission-control__navList">
            {navItems.map(([Icon, label]) => (
              <button
                type="button"
                key={label}
                className={`agent-mission-control__navItem ${label === 'Office' ? 'is-active' : ''}`}
              >
                <Icon size={15} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </aside>

        <main className="agent-mission-control__main">
          <div className="agent-mission-control__toolbar">
            <div className="agent-mission-control__searchWrap">
              <Search size={14} className="agent-mission-control__searchIcon" />
              <CFormInput
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search agents"
                className="agent-mission-control__searchInput"
              />
            </div>
            <div className="agent-mission-control__toolbarButtons">
              <CButton color="dark" variant="ghost" size="sm"><Pause size={14} className="me-1" /> Pause</CButton>
              <CButton color="dark" variant="ghost" size="sm" onClick={onRefresh}><RefreshCw size={14} className="me-1" /> Refresh</CButton>
              <CButton color="dark" variant="ghost" size="sm"><Eye size={14} /></CButton>
            </div>
          </div>

          {error ? <CAlert color="danger" className="mb-3">{error}</CAlert> : null}

          {loading ? (
            <div className="agent-mission-control__loading">
              <CSpinner color="info" />
            </div>
          ) : (
            <>
              <section className="agent-mission-control__officeCard">
                <div className="agent-mission-control__officeBoard">
                  <div className="agent-mission-control__officeTop">
                    <CButton color="primary" size="sm" onClick={onCreateAgent}>+ Start Agent</CButton>
                    <div className="agent-mission-control__chips">
                      <CBadge color="info">{visibleAgents.length} visible</CBadge>
                      <CBadge color="secondary">{hydratedAgents.length} total</CBadge>
                    </div>
                  </div>
                  <div className="agent-mission-control__floor">
                    {visibleAgents.map((agent) => {
                      const pos = positions[agent.id] || { x: agent.x, y: agent.y };
                      const color = agentColor(agent.name || 'Agent');
                      const selected = activeAgent?.id === agent.id;
                      return (
                        <button
                          type="button"
                          key={agent.id}
                          className={`agent-mission-control__pixelAgent ${selected ? 'is-selected' : ''}`}
                          style={{ left: pos.x, top: pos.y }}
                          onClick={() => setSelectedId(agent.id)}
                          title={agent.name}
                        >
                          <span className="agent-mission-control__pixelHead" style={{ backgroundColor: color }} />
                          <span className="agent-mission-control__pixelBody" style={{ backgroundColor: color }} />
                          <span className={`agent-mission-control__statusDot is-${agent.officeStatus}`} />
                          <span className="agent-mission-control__nameTag" style={{ color }}>{agent.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              <section className="agent-mission-control__activityCard">
                <div className="agent-mission-control__activityTitle">
                  <Activity size={15} className="me-2" /> Live Activity
                </div>
                {activityLoading ? (
                  <div className="agent-mission-control__activityLoading">
                    <CSpinner size="sm" color="info" />
                  </div>
                ) : activityItems.length ? (
                  <div className="agent-mission-control__activityList">
                    {activityItems.map((item) => (
                      <article key={item.id} className="agent-mission-control__activityItem">
                        <div className="agent-mission-control__activitySummary">
                          {item.summary || 'Conversation update'}
                        </div>
                        <div className="agent-mission-control__activityMeta">
                          <span>{item.agentName}</span>
                          <span>{relativeTime(item.createdAt)}</span>
                          <CBadge color="secondary">{item.sentiment || 'neutral'}</CBadge>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="agent-mission-control__muted">No recent events for this workspace.</p>
                )}
              </section>

              <section className="agent-mission-control__grid">
                {hydratedAgents.map((agent) => {
                  const isSelected = activeAgent?.id === agent.id;
                  const color = agentColor(agent.name || 'Agent');
                  const normalized = normalizeStatus(agent.status);
                  return (
                    <article
                      key={agent.id}
                      className={`agent-mission-control__agentCard ${isSelected ? 'is-selected' : ''}`}
                    >
                      <button type="button" className="agent-mission-control__agentCardMain" onClick={() => setSelectedId(agent.id)}>
                        <div className="agent-mission-control__agentHeader">
                          <span className="agent-mission-control__agentSwatch" style={{ backgroundColor: color }} />
                          <div>
                            <h6>{agent.name}</h6>
                            <small>{agent.personality || 'No profile summary yet'}</small>
                          </div>
                        </div>
                        <div className="agent-mission-control__agentFooter">
                          <span>{statusText(agent.officeStatus)}</span>
                          <CBadge color={normalized === 'active' ? 'success' : 'secondary'}>{normalized}</CBadge>
                        </div>
                      </button>
                      <div className="agent-mission-control__actions">
                        <CButton color="info" size="sm" variant="ghost" onClick={() => onOpenAgent(agent)}>
                          <MessageSquare size={14} className="me-1" /> Open
                        </CButton>
                        <CButton color="warning" size="sm" variant="ghost" onClick={() => onToggleStatus(agent)}>
                          Toggle
                        </CButton>
                        <CButton color="danger" size="sm" variant="ghost" onClick={() => onDeleteAgent(agent)}>
                          Delete
                        </CButton>
                      </div>
                    </article>
                  );
                })}
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

AgentMissionControlBoard.propTypes = {
  agents: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    status: PropTypes.string,
    personality: PropTypes.string,
    role: PropTypes.string,
  })).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  activityItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    agentName: PropTypes.string,
    summary: PropTypes.string,
    sentiment: PropTypes.string,
    createdAt: PropTypes.string,
  })),
  activityLoading: PropTypes.bool,
  officeName: PropTypes.string,
  onCreateAgent: PropTypes.func,
  onOpenAgent: PropTypes.func,
  onToggleStatus: PropTypes.func,
  onDeleteAgent: PropTypes.func,
  onRefresh: PropTypes.func,
};

AgentMissionControlBoard.defaultProps = {
  loading: false,
  error: null,
  activityItems: [],
  activityLoading: false,
  officeName: 'Mission Control',
  onCreateAgent: () => {},
  onOpenAgent: () => {},
  onToggleStatus: () => {},
  onDeleteAgent: () => {},
  onRefresh: () => {},
};

export default AgentMissionControlBoard;
